import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/features/coach.css';
import PublicStockCard, {
  PublicStockCardSkeleton,
  PublicStockCardError,
  PublicStockCardData,
} from './PublicStockCard';
import PublicContractCard, {
  PublicContractCardSkeleton,
  PublicContractCardError,
  PublicContractCardData,
} from './PublicContractCard';
import PublicPositionCard, {
  PublicPositionCardSkeleton,
  PublicPositionCardError,
  PublicPositionCardData,
} from './PublicPositionCard';
import CoachIncomeForm, { IncomeFormFields } from './CoachIncomeForm';
import PublicIncomeCard, { IncomeData } from './PublicIncomeCard';
import {
  BanknotesIcon,
  ArrowPathIcon,
  ScaleIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Ticker linkify: a token is a candidate ticker if it's 2–5 uppercase letters
 * (optionally $-prefixed) and not a common non-ticker acronym. We split the
 * assistant text on these candidates, wrap matches in clickable chips, and let
 * the click handler confirm via the API (a 404 demotes the token to plain text).
 */
const TICKER_RE = /(\$?[A-Z]{2,5})\b/g;
const TICKER_STOPLIST = new Set([
  'IV', 'OTM', 'ITM', 'ATM', 'DTE', 'CC', 'CSP', 'ETF', 'AI', 'US', 'USA',
  'EPS', 'PE', 'ROI', 'IRA', 'API', 'FAQ', 'OK', 'YES', 'NO', 'PM', 'AM',
  'EST', 'UTC', 'NYSE', 'SEC', 'IRS', 'GDP', 'CPI', 'FED',
]);

/**
 * Contract link: the coach emits a specific option contract as a markdown-style
 * token `[label](contract:SYMBOL:STRIKE:YYYY-MM-DD:TYPE)`. We detect these in the
 * settled text and render `label` as a clickable contract chip (distinct from a
 * ticker chip). The 4 colon-delimited fields after `contract:` are machine-readable.
 */
const CONTRACT_RE =
  /\[([^\]]+)\]\(contract:([A-Za-z.]{1,6}):([0-9]+(?:\.[0-9]+)?):(\d{4}-\d{2}-\d{2}):(call|put)\)/g;

interface ContractParams {
  symbol: string;
  strike: number;
  expiry: string;
  type: 'call' | 'put';
}

/** Composite cache key for a contract: SYMBOL|STRIKE|EXPIRY|TYPE. */
const contractKey = (p: ContractParams): string =>
  `${p.symbol}|${p.strike}|${p.expiry}|${p.type}`;

/**
 * Position link (the roll/hold/close decision card): the coach emits a
 * `[label](position:<querystring>)` token where the querystring carries the
 * position params, e.g.
 *   [See your roll/hold/close call →](position:symbol=AAPL&strike=305&expiry=2026-07-24&type=call&cost=280&entry=6&n=1)
 * The entry-date variant uses `&date=2026-05-01` instead of `&entry=`. The
 * querystring body is `[^)\s]+` so it never swallows the closing paren or
 * crosses whitespace, and `position:` is a distinct prefix that can't collide
 * with `contract:` or a bare ticker (matched FIRST in the linkify pass).
 */
const POSITION_RE = /\[([^\]]+)\]\(position:([^)\s]+)\)/g;

interface PositionParams {
  symbol: string;
  strike: number;
  expiry: string;
  type: 'call' | 'put';
  cost_basis: number | null;
  entry_premium: number | null;
  entry_date: string | null;
  num_contracts: number | null;
  current_price: number | null;
}

/**
 * Parse a `position:` querystring into POST-body params, or null if the
 * required fields (symbol/strike/expiry/type) are missing/unparseable.
 * `cost` → cost_basis, `entry` → entry_premium, `date` → entry_date, `n` →
 * num_contracts, `price` → current_price. A token that doesn't parse falls back
 * to plain-text label. `price` is carried only when the user explicitly stated
 * the current stock price (a hypothetical / "what if"); when absent the card
 * resolves the live price itself.
 */
const parsePositionParams = (qs: string): PositionParams | null => {
  let sp: URLSearchParams;
  try {
    sp = new URLSearchParams(qs);
  } catch {
    return null;
  }
  const symbol = (sp.get('symbol') || '').toUpperCase().replace(/\.$/, '');
  const strikeRaw = sp.get('strike');
  const expiry = sp.get('expiry') || '';
  const typeRaw = sp.get('type');
  const strike = strikeRaw == null ? NaN : parseFloat(strikeRaw);
  if (
    !symbol ||
    !Number.isFinite(strike) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(expiry) ||
    (typeRaw !== 'call' && typeRaw !== 'put')
  ) {
    return null;
  }
  const numOrNull = (v: string | null): number | null => {
    if (v == null) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const dateRaw = sp.get('date');
  return {
    symbol,
    strike,
    expiry,
    type: typeRaw,
    cost_basis: numOrNull(sp.get('cost')),
    entry_premium: numOrNull(sp.get('entry')),
    entry_date: dateRaw && /^\d{4}-\d{2}-\d{2}$/.test(dateRaw) ? dateRaw : null,
    num_contracts: numOrNull(sp.get('n')),
    current_price: numOrNull(sp.get('price')),
  };
};

/** Composite cache key for a position card. Includes the entry inputs so two
 *  cards on the same strike with different cost/premium don't collide. */
const positionKey = (p: PositionParams): string =>
  `${p.symbol}|${p.strike}|${p.expiry}|${p.type}|${p.cost_basis ?? ''}|${p.entry_premium ?? ''}|${p.entry_date ?? ''}|${p.num_contracts ?? ''}|${p.current_price ?? ''}`;

/**
 * Safe prefix of the actively-streaming assistant text for live linkifying.
 *
 * We linkify the streaming bubble too (so tickers / contract / decision chips
 * are clickable the moment they appear), but a `[label](contract:…)` or
 * `[label](position:…)` token's label contains spaces, so the word-by-word
 * reveal exposes it across several ticks. While it's still forming we must not
 * render its raw brackets — or worse, let `linkifyTickers` chip the SYMBOL
 * inside the half-typed token. So if the text's last `[` hasn't yet closed into
 * a complete `[label](…)` token, trim from that `[`; the next ticks complete the
 * token and it appears as a chip. (The model emits no other literal `[`, per the
 * prose-only system prompt, so this only ever holds back a forming chip.)
 */
const streamingSafeText = (text: string): string => {
  const open = text.lastIndexOf('[');
  if (open === -1) return text;
  // Already a complete token at the last '['? Then nothing is forming — keep all.
  if (/^\[[^\]]*\]\([^)]*\)/.test(text.slice(open))) return text;
  return text.slice(0, open);
};

/**
 * Strip markdown the model occasionally emits despite the prose-only system
 * prompt. This chat renders PLAIN TEXT (no markdown parser), so a stray ** or a
 * leading "1." would otherwise show literally. Removes bold/italic markers and
 * line-leading heading / list markers, leaving the words intact. Guarded so it
 * never eats decimals ("1.5%"), negative dollar amounts ("-$3.93"), or the
 * [label](contract:…|position:…) link tokens — those contain no * and never
 * begin a line with a bare "1. " / "- " marker (the require-trailing-space rules
 * below skip "1.5" and "-$3" which have no space after the dot / dash).
 */
const stripMarkdown = (text: string): string =>
  text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]*[-+*][ \t]+/gm, '')
    .replace(/^[ \t]*\d+\.[ \t]+/gm, '');

/** Card-fetch state per symbol, shared across all messages. */
type CardState = PublicStockCardData | 'loading' | 'error' | 'notfound';

/** Contract-card fetch state per composite key, shared across all messages. */
type ContractCardState =
  | PublicContractCardData
  | 'loading'
  | 'error'
  | 'notfound';

/** Position-card fetch state per composite key, shared across all messages. */
type PositionCardState =
  | PublicPositionCardData
  | 'loading'
  | 'error'
  | 'notfound';

type Role = 'user' | 'assistant';
interface Message {
  role: Role;
  content: string;
  /** marks an assistant bubble that holds an error / rate-limit notice */
  isError?: boolean;
}

/** Stagger helper for the load-in reveal. */
const reveal = (step: number) =>
  ({ '--d': `${step * 90}ms` } as React.CSSProperties);

// Order matters: the first three pay off the three Reddit-ad hooks
// (phantom income, roll-vs-assign, JEPQ-vs-DIY) so a cold paid visitor lands
// and sees the exact question their ad teased as tap #1 — zero effort to reach
// Q1, the funnel's real drop-off point. Generic ticker examples follow.
const STARTERS: string[] = [
  'Is my covered-call income real, or am I just eating my principal?',
  // Roll-vs-assign hook. Replaced at runtime with a LIVE, currently-ITM scenario
  // (GET /sample-roll-scenario) so the numbers never go stale; this evergreen
  // phrasing is only the fallback shown if that fetch fails.
  "I sold a covered call that's now in the money with about a week left — should I roll it or let it get assigned?",
  'Why run my own covered calls instead of just buying JEPQ?',
  'Is now a good time to sell a covered call on AAPL? What does the data say?',
  "What strike and expiration should I pick for a covered call on MSFT if I don't want my shares called away?",
];

/** Compact icon + short label for each STARTERS entry (same index). Used by the
 *  active-conversation left rail so it reads as a tidy menu, not a wall of full
 *  questions. Tapping still sends the full starter text. */
const STARTER_META: { label: string; Icon: typeof BanknotesIcon }[] = [
  { label: 'Real income?', Icon: BanknotesIcon },
  { label: 'Roll or assign?', Icon: ArrowPathIcon },
  { label: 'DIY vs JEPQ?', Icon: ScaleIcon },
  { label: 'Good entry?', Icon: ClockIcon },
  { label: 'Which strike?', Icon: AdjustmentsHorizontalIcon },
];

/** Ad → use-case router. The visitor's utm_campaign decides which first-screen
 *  action fires, so the landing matches the ad they clicked. Unknown / organic /
 *  no-UTM → 'generic' (the open chat). Adding a new ad = one line here. */
type UseCase = 'income' | 'roll' | 'setup' | 'generic';
const CAMPAIGN_USE_CASE: Record<string, UseCase> = {
  phantom_income: 'income',
  roll_vs_assign: 'roll',
  jepq_vs_diy: 'setup',
};
/** Hero copy per use case. Only the income use case also renders the income form
 *  below (which carries its own ad-matched framing); the others lead with their
 *  matching starter chip routed through the coach's tools. */
const USE_CASE_HERO: Record<UseCase, { h1: string; deck: string }> = {
  income: {
    h1: 'Straight answers about your covered calls.',
    deck: 'Is your income real — or return of capital? Fill in your position below and get the honest split, in plain English. No tips, no hype.',
  },
  roll: {
    h1: 'Roll it, or let it get assigned?',
    deck: 'Describe the call you sold and I’ll show what the discipline points to for each goal — keep the shares, cycle for income, or harvest the premium. No advice, just the read.',
  },
  setup: {
    h1: 'Run your own calls — or just buy JEPQ?',
    deck: 'Name a stock and I’ll show how its covered-call setup actually scores: the track record, the timing, and the premium on offer.',
  },
  generic: {
    h1: 'Straight answers about your covered calls.',
    deck: 'Roll, or let it get assigned? Is your income real — or return of capital? Better than just holding JEPQ? Ask, and get what the numbers actually say, in plain English. No tips, no hype, no “you’ll beat the market.”',
  },
};
/** Starter chip (index into STARTERS) to surface first for a use case. */
const USE_CASE_LEAD_CHIP: Partial<Record<UseCase, number>> = {
  roll: 1,   // the live roll-vs-assign scenario
  setup: 2,  // the JEPQ-vs-DIY chip
};

const RATE_LIMIT_TEXT =
  "You've hit today's free question limit. Drop your email to keep going — it's free, and it lifts the limit right away.";

/** Show the email modal after this many user questions. */
const FREE_QUESTIONS = 3;
const LS_UNLOCKED = 'babylon_coach_unlocked';
const LS_EMAIL = 'babylon_coach_email';
const LS_SESSION = 'babylon_coach_session';

/** Stable anonymous session id for server-side funnel stitching (pageview →
 *  questions → unlock). Generated once per browser, persisted in localStorage.
 *  Falls back to an ephemeral per-load id when storage is blocked. */
function getCoachSessionId(): string {
  const fresh = () =>
    `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    let id = localStorage.getItem(LS_SESSION);
    if (!id) {
      id = window.crypto?.randomUUID?.() || fresh();
      localStorage.setItem(LS_SESSION, id);
    }
    return id;
  } catch {
    return fresh();
  }
}

/** Best-effort server-side funnel beacon. Mirrors trackGoal (Plausible) but
 *  lands in OUR DB (public_coach_events) so the funnel is queryable for the
 *  founder /admin/coach dashboard + the daily CEO digest. Never throws. */
function trackEvent(
  eventType: 'pageview' | 'register_click',
  attribution: {
    session_id: string;
    utm_source: string | null;
    utm_campaign: string | null;
    referrer: string | null;
  },
): void {
  try {
    fetch(`${API_URL}/api/public/coach/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ event_type: eventType, ...attribution }),
    }).catch(() => {
      /* best-effort */
    });
  } catch {
    /* best-effort */
  }
}

/** Fire a Plausible custom goal if the script is loaded (no-op otherwise).
 *  Lets us read the coach funnel (visitor → question → email → register) instead
 *  of just pageviews, without coupling this component to the analytics global
 *  typing. Optional `props` ride along as Plausible custom properties. */
function trackGoal(event: string, props?: Record<string, string | number>): void {
  try {
    const fn = (
      window as unknown as {
        plausible?: (e: string, opts?: { props?: Record<string, string | number> }) => void;
      }
    ).plausible;
    fn?.(event, props ? { props } : undefined);
  } catch {
    /* analytics is best-effort — never let it break the flow */
  }
}

/** The chat's plain-English VERDICT for an income-card result — the "so what."
 *  Deliberately carries NO numbers (the card owns the math), so the two panels
 *  complement instead of duplicate: chat = meaning, card = the figures. */
function incomeNarration(d: IncomeData): string {
  if (d.total_pnl == null || d.stock_leg == null) {
    return "Your premium income is real — it’s the time value you’ve harvested as the option decays toward expiry, and it’s yours whether the stock rises or falls. I can’t weigh it against your shares without your cost basis, but the locked figure is on the card. →";
  }
  const capped = d.call_vs_holding != null && d.call_vs_holding < 0;
  if (d.total_pnl >= 0) {
    if (capped) {
      return "Your income is real and you’re green overall — but the stock ran past your strike, so the call capped some of the upside. The premium you’ve kept is genuine time-value decay; the only “cost” is the extra gain you gave up by selling the call, not eaten income. Full split on the card. →";
    }
    return "Your income is real and the whole position is green — the premium you’ve kept is genuine time-value decay (not return of capital), and your shares have held up, so both legs are working for you. Full split on the card. →";
  }
  return "Your income is real and working — the premium you’ve kept is genuine time-value decay, yours no matter what the stock does. The position is only in the red because the shares slipped below your cost basis; that’s a paper loss on the stock, not your income being fake or handed back to you. Full split on the card. →";
}

/** Modal flavours: friendly email nudge, hard email gate, and the final
 *  register gate (today's allowance used up — an account keeps it going now). */
type ModalMode = 'nudge' | 'gate' | 'register';

/** Copy for the final register gate (no email field — they already gave one). */
const REGISTER_GATE_TEXT =
  "You've used your free questions for today — create a free account to keep going.";

const PublicCoach: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source');
  const utmCampaign = searchParams.get('utm_campaign');

  // Ad → use-case router: which first-screen action fires (matches the ad clicked).
  // Unknown / organic → 'generic' (the open chat).
  const useCase: UseCase = (utmCampaign && CAMPAIGN_USE_CASE[utmCampaign]) || 'generic';
  const hero = USE_CASE_HERO[useCase];

  // Build the /register path with attribution forwarded from the coach URL, so a
  // coach → register conversion is attributable in analytics (the email lead is
  // already tagged server-side via WtpIntent, but the eventual signup wasn't).
  // We forward utm_source/medium only when present (never fabricate a source),
  // default utm_campaign to 'ai_coach' (same as the unlock lead), and stamp
  // utm_content so the coach surface is distinguishable from other /register
  // entry points. `plan='pro'` pre-selects Pro on the register page.
  const registerPath = useCallback(
    (plan?: 'pro') => {
      const p = new URLSearchParams();
      if (plan) p.set('plan', plan);
      if (utmSource) p.set('utm_source', utmSource);
      const utmMedium = searchParams.get('utm_medium');
      if (utmMedium) p.set('utm_medium', utmMedium);
      p.set('utm_campaign', utmCampaign || 'ai_coach');
      p.set('utm_content', 'coach_register');
      return `/register?${p.toString()}`;
    },
    [utmSource, utmCampaign, searchParams]
  );

  // Stable anonymous session id + the attribution payload sent on EVERY funnel
  // event (pageview, question, unlock) so the Reddit-ad → email funnel is
  // queryable server-side, not just in Plausible. utm rides from the URL params.
  const sessionIdRef = useRef<string>(getCoachSessionId());
  const buildAttribution = useCallback(
    () => ({
      session_id: sessionIdRef.current,
      utm_source: utmSource ? utmSource.slice(0, 60) : null,
      utm_campaign: (utmCampaign || 'ai_coach').slice(0, 120),
      referrer: (typeof document !== 'undefined' ? document.referrer : '').slice(0, 2000) || null,
    }),
    [utmSource, utmCampaign],
  );

  // Fire a server-side pageview ONCE per mount (top-of-funnel: page reaches by
  // source). Ref-guarded so React 18 StrictMode's double-invoke can't double-log.
  const pageviewSentRef = useRef(false);
  useEffect(() => {
    if (pageviewSentRef.current) return;
    pageviewSentRef.current = true;
    trackEvent('pageview', buildAttribution());
  }, [buildAttribution]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  // Starter chips. The roll-vs-assign chip (index 1) is swapped at mount for a
  // live, currently-ITM scenario so its numbers never go stale; until then (and
  // on fetch failure) the evergreen STARTERS fallback shows.
  const [starters, setStarters] = useState<string[]>(STARTERS);

  // email capture / unlock
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);
  // Whether we've already auto-shown the question-3 nudge this session.
  const nudgeShownRef = useRef(false);
  // Live mirror of `unlocked` for use inside async callbacks without re-binding.
  const unlockedRef = useRef(false);
  // Count of user questions sent this session.
  const questionCountRef = useRef(0);
  // The user question that triggered the HARD GATE (blocked send). When set,
  // a successful email unlock auto-answers it instead of leaving it stranded.
  const pendingQuestionRef = useRef<string | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  // Message count at the last scroll-anchor, so we reposition only when a NEW
  // turn is added — never on the streaming content updates that grow the last
  // message (those must not fight the reader's own scrolling).
  const anchoredCount = useRef(0);
  // Touch-start Y for the mobile bottom-sheet swipe-down-to-dismiss.
  const sheetTouchY = useRef<number | null>(null);
  const emailValid = /\S+@\S+\.\S+/.test(email.trim());

  // --- ticker linkify + stock cards --------------------------------------
  // Per-symbol card state (data / loading / error / notfound). 'notfound'
  // permanently demotes a ticker-shaped word back to plain text (no error
  // shown) so false positives quietly stop being clickable after one check.
  const [cardData, setCardData] = useState<Record<string, CardState>>({});
  // Per-contract card state (keyed by SYMBOL|STRIKE|EXPIRY|TYPE), shared across
  // all messages — same lifecycle semantics as cardData.
  const [contractCardData, setContractCardData] = useState<
    Record<string, ContractCardState>
  >({});
  // Per-position card state (keyed by the composite positionKey), shared across
  // all messages — same lifecycle semantics as cardData / contractCardData.
  const [positionCardData, setPositionCardData] = useState<
    Record<string, PositionCardState>
  >({});
  // The symbol whose card is currently revealed under message index `i`,
  // keyed by message index. Clicking the same chip again collapses it.
  // This is the MOBILE / single-column surface (inline under the message).
  const [openCard, setOpenCard] = useState<Record<number, string | null>>({});
  // Same inline (mobile) reveal surface, but for contract chips: the contract
  // key currently open under message index `i`. Stock + contract inline reveals
  // are mutually exclusive per message (opening one closes the other).
  const [openContract, setOpenContract] = useState<
    Record<number, string | null>
  >({});
  // Same inline (mobile) reveal surface, for position chips: the position key
  // currently open under message index `i`. Stock / contract / position inline
  // reveals are mutually exclusive per message (opening one closes the others).
  const [openPosition, setOpenPosition] = useState<
    Record<number, string | null>
  >({});
  // The single "current artifact" shown in the DESKTOP right panel — the
  // most-recently-opened ticker, contract, OR position. A new click of any kind
  // replaces it; the panel's close (×) clears it back to the empty hint. CSS
  // decides which surface is shown (right panel ≥980px, inline reveal <980px),
  // so both are driven from the same click handlers and only one is visible.
  type Artifact =
    | { kind: 'stock'; symbol: string }
    | { kind: 'contract'; key: string; params: ContractParams }
    | { kind: 'position'; key: string; params: PositionParams }
    | { kind: 'income'; data: IncomeData };
  const [currentArtifact, setCurrentArtifact] = useState<Artifact | null>(null);
  // The income answer is rendered INLINE in the center (its own 2-col card), not in
  // the right artifact panel — so the page isn't right-heavy with an empty middle.
  const [incomeResult, setIncomeResult] = useState<IncomeData | null>(null);

  // Position tokens we've already AUTO-opened, keyed by `${msgIndex}|${positionKey}`.
  // Auto-open fires at most once per (message, position) pair so streaming ticks
  // and re-renders don't re-trigger it; a genuinely new position token in a later
  // message still auto-opens (different msgIndex). The inline chip stays clickable
  // as a manual re-open affordance regardless. Only POSITION tokens auto-open —
  // tickers/contracts the coach name-drops in passing must not flicker the panel.
  const autoOpenedPositionsRef = useRef<Set<string>>(new Set());

  /**
   * Fetch a card (once), toggle it inline under `msgIndex` (mobile surface),
   * and promote it to the current desktop artifact (right panel). A 404
   * demotes the ticker to plain text and clears it from both surfaces.
   */
  const handleTickerClick = useCallback(
    async (rawSymbol: string, msgIndex: number) => {
      const symbol = rawSymbol.replace(/^\$/, '').toUpperCase();

      // Inline (mobile) surface: toggle open/closed under this message, and
      // close any contract reveal open under the same message (mutually exclusive).
      setOpenCard((prev) => {
        const current = prev[msgIndex];
        return { ...prev, [msgIndex]: current === symbol ? null : symbol };
      });
      setOpenContract((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      setOpenPosition((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      // Desktop artifact: a click always promotes/replaces (no toggle) — the
      // right panel shows the most-recently-opened ticker.
      setCurrentArtifact({ kind: 'stock', symbol });

      // Already resolved (data / notfound / error) — no refetch.
      const existing = cardData[symbol];
      if (existing && existing !== 'loading') {
        // If this was already known-bad, don't leave it pinned in the panel.
        if (existing === 'notfound') {
          setCurrentArtifact((prev) =>
            prev?.kind === 'stock' && prev.symbol === symbol ? null : prev
          );
        }
        return;
      }

      setCardData((prev) => ({ ...prev, [symbol]: 'loading' }));
      try {
        const res = await fetch(
          `${API_URL}/api/public/coach/stock-card/${encodeURIComponent(symbol)}`
        );
        if (res.status === 404) {
          // Not a real ticker — demote to plain text everywhere, and don't
          // leave a card open for it on either surface.
          setCardData((prev) => ({ ...prev, [symbol]: 'notfound' }));
          setOpenCard((prev) =>
            prev[msgIndex] === symbol ? { ...prev, [msgIndex]: null } : prev
          );
          setCurrentArtifact((prev) =>
            prev?.kind === 'stock' && prev.symbol === symbol ? null : prev
          );
          return;
        }
        if (!res.ok) {
          setCardData((prev) => ({ ...prev, [symbol]: 'error' }));
          return;
        }
        const data: PublicStockCardData = await res.json();
        setCardData((prev) => ({ ...prev, [symbol]: data }));
      } catch {
        setCardData((prev) => ({ ...prev, [symbol]: 'error' }));
      }
    },
    [cardData]
  );

  /**
   * Fetch a contract card (once), toggle it inline under `msgIndex` (mobile),
   * and promote it to the current desktop artifact (right panel) — mirrors
   * handleTickerClick. A 404 demotes the contract chip to plain text and clears
   * it from both surfaces.
   */
  const handleContractClick = useCallback(
    async (params: ContractParams, msgIndex: number) => {
      const key = contractKey(params);

      // Inline (mobile) surface: toggle open/closed under this message, and
      // close any stock reveal on the same message (mutually exclusive).
      setOpenContract((prev) => {
        const current = prev[msgIndex];
        return { ...prev, [msgIndex]: current === key ? null : key };
      });
      setOpenCard((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      setOpenPosition((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      // Desktop artifact: always promote/replace (no toggle).
      setCurrentArtifact({ kind: 'contract', key, params });

      // Already resolved — no refetch.
      const existing = contractCardData[key];
      if (existing && existing !== 'loading') {
        if (existing === 'notfound') {
          setCurrentArtifact((prev) =>
            prev?.kind === 'contract' && prev.key === key ? null : prev
          );
        }
        return;
      }

      setContractCardData((prev) => ({ ...prev, [key]: 'loading' }));
      try {
        const qs = new URLSearchParams({
          symbol: params.symbol,
          strike: String(params.strike),
          expiry: params.expiry,
          type: params.type,
        });
        const res = await fetch(
          `${API_URL}/api/public/coach/contract-card?${qs.toString()}`
        );
        if (res.status === 404) {
          // No such contract in the latest snapshot — demote to plain text
          // everywhere, and don't leave a card open on either surface.
          setContractCardData((prev) => ({ ...prev, [key]: 'notfound' }));
          setOpenContract((prev) =>
            prev[msgIndex] === key ? { ...prev, [msgIndex]: null } : prev
          );
          setCurrentArtifact((prev) =>
            prev?.kind === 'contract' && prev.key === key ? null : prev
          );
          return;
        }
        if (!res.ok) {
          setContractCardData((prev) => ({ ...prev, [key]: 'error' }));
          return;
        }
        const data: PublicContractCardData = await res.json();
        setContractCardData((prev) => ({ ...prev, [key]: data }));
      } catch {
        setContractCardData((prev) => ({ ...prev, [key]: 'error' }));
      }
    },
    [contractCardData]
  );

  /**
   * Fetch a position decision card (once), toggle it inline under `msgIndex`
   * (mobile), and promote it to the current desktop artifact (right panel) —
   * mirrors handleContractClick. A failed/empty response (404 or any error
   * where the engine couldn't build a card) demotes the chip to plain text
   * and clears it from both surfaces, same as the other 404 paths.
   */
  const handlePositionClick = useCallback(
    async (params: PositionParams, msgIndex: number) => {
      const key = positionKey(params);

      // Inline (mobile) surface: toggle open/closed under this message, and
      // close any stock / contract reveal on the same message (mutually excl.).
      setOpenPosition((prev) => {
        const current = prev[msgIndex];
        return { ...prev, [msgIndex]: current === key ? null : key };
      });
      setOpenCard((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      setOpenContract((prev) =>
        prev[msgIndex] != null ? { ...prev, [msgIndex]: null } : prev
      );
      // Desktop artifact: always promote/replace (no toggle).
      setCurrentArtifact({ kind: 'position', key, params });

      // Already resolved — no refetch.
      const existing = positionCardData[key];
      if (existing && existing !== 'loading') {
        if (existing === 'notfound') {
          setCurrentArtifact((prev) =>
            prev?.kind === 'position' && prev.key === key ? null : prev
          );
        }
        return;
      }

      setPositionCardData((prev) => ({ ...prev, [key]: 'loading' }));
      try {
        const res = await fetch(
          `${API_URL}/api/public/coach/position-card`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              symbol: params.symbol,
              strike: params.strike,
              expiry: params.expiry,
              type: params.type,
              cost_basis: params.cost_basis,
              entry_premium: params.entry_premium,
              entry_date: params.entry_date,
              num_contracts: params.num_contracts,
              current_price: params.current_price,
            }),
          }
        );
        if (res.status === 404) {
          // Couldn't build a decision for this position — demote to plain text
          // everywhere, and don't leave a card open on either surface.
          setPositionCardData((prev) => ({ ...prev, [key]: 'notfound' }));
          setOpenPosition((prev) =>
            prev[msgIndex] === key ? { ...prev, [msgIndex]: null } : prev
          );
          setCurrentArtifact((prev) =>
            prev?.kind === 'position' && prev.key === key ? null : prev
          );
          return;
        }
        if (!res.ok) {
          setPositionCardData((prev) => ({ ...prev, [key]: 'error' }));
          return;
        }
        const data: PublicPositionCardData = await res.json();
        // A response that's structurally empty (no position payload) is treated
        // like a 404 — demote rather than render a blank card.
        if (!data || !data.position) {
          setPositionCardData((prev) => ({ ...prev, [key]: 'notfound' }));
          setOpenPosition((prev) =>
            prev[msgIndex] === key ? { ...prev, [msgIndex]: null } : prev
          );
          setCurrentArtifact((prev) =>
            prev?.kind === 'position' && prev.key === key ? null : prev
          );
          return;
        }
        setPositionCardData((prev) => ({ ...prev, [key]: data }));
      } catch {
        setPositionCardData((prev) => ({ ...prev, [key]: 'error' }));
      }
    },
    [positionCardData]
  );

  /** Render the current artifact's body (skeleton / error / card). */
  const renderArtifactBody = useCallback(
    (state: CardState | undefined): React.ReactNode => {
      if (state === 'loading' || state === undefined) {
        return <PublicStockCardSkeleton />;
      }
      if (state === 'error') return <PublicStockCardError />;
      if (state === 'notfound') return null;
      return <PublicStockCard data={state} />;
    },
    []
  );

  /** Render the current contract artifact's body (skeleton / error / card). */
  const renderContractBody = useCallback(
    (state: ContractCardState | undefined): React.ReactNode => {
      if (state === 'loading' || state === undefined) {
        return <PublicContractCardSkeleton />;
      }
      if (state === 'error') return <PublicContractCardError />;
      if (state === 'notfound') return null;
      return <PublicContractCard data={state} />;
    },
    []
  );

  /** Render the current position artifact's body (skeleton / error / card). */
  const renderPositionBody = useCallback(
    (state: PositionCardState | undefined): React.ReactNode => {
      if (state === 'loading' || state === undefined) {
        return <PublicPositionCardSkeleton />;
      }
      if (state === 'error') return <PublicPositionCardError />;
      if (state === 'notfound') return null;
      return <PublicPositionCard data={state} />;
    },
    []
  );

  /**
   * Linkify a plain-text segment (no contract tokens) — wraps candidate tickers
   * in clickable chips. Factored out so renderLinkified can apply it to the
   * gaps BETWEEN contract tokens without the ticker regex ever seeing a token's
   * internal SYMBOL / CALL / PUT words.
   */
  const linkifyTickers = useCallback(
    (text: string, msgIndex: number, keyBase: string): React.ReactNode[] => {
      const out: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      TICKER_RE.lastIndex = 0;
      let key = 0;
      while ((match = TICKER_RE.exec(text)) !== null) {
        const token = match[1]; // possibly $-prefixed
        const bare = token.replace(/^\$/, '');
        const isCandidate = !TICKER_STOPLIST.has(bare) && cardData[bare] !== 'notfound';
        if (!isCandidate) continue; // leave it in place as plain text
        if (match.index > lastIndex) {
          out.push(text.slice(lastIndex, match.index));
        }
        out.push(
          <button
            key={`tk-${keyBase}-${key++}`}
            type="button"
            className="cch-ticker"
            onClick={() => handleTickerClick(bare, msgIndex)}
          >
            {token}
          </button>
        );
        lastIndex = match.index + token.length;
      }
      if (lastIndex < text.length) out.push(text.slice(lastIndex));
      return out;
    },
    [cardData, handleTickerClick]
  );

  /**
   * Linkify a segment KNOWN to contain no `position:` tokens — wraps contract
   * tokens (`[label](contract:…)`) FIRST (unambiguous markdown-style tokens),
   * then ticker-linkifies the gaps between them (so the ticker regex never sees
   * a contract token's internal SYMBOL/CALL/PUT words). Contract tokens known
   * to be 'notfound' fall back to rendering just their label text.
   */
  const linkifyContracts = useCallback(
    (text: string, msgIndex: number, keyBase: string): React.ReactNode[] => {
      const out: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      CONTRACT_RE.lastIndex = 0;
      let cKey = 0;
      while ((match = CONTRACT_RE.exec(text)) !== null) {
        const [, label, symRaw, strikeRaw, expiry, typeRaw] = match;
        // Ticker-linkify the plain text before this token.
        if (match.index > lastIndex) {
          out.push(
            ...linkifyTickers(
              text.slice(lastIndex, match.index),
              msgIndex,
              `${keyBase}-${cKey}`
            )
          );
        }
        const params: ContractParams = {
          symbol: symRaw.toUpperCase().replace(/\.$/, ''),
          strike: parseFloat(strikeRaw),
          expiry,
          type: typeRaw as 'call' | 'put',
        };
        const key = contractKey(params);
        if (contractCardData[key] === 'notfound') {
          // Known-bad contract — render just the human label as plain text.
          out.push(label);
        } else {
          out.push(
            <button
              key={`ct-${keyBase}-${cKey++}`}
              type="button"
              className="cch-contract"
              onClick={() => handleContractClick(params, msgIndex)}
            >
              {label}
            </button>
          );
        }
        lastIndex = match.index + match[0].length;
      }
      // Ticker-linkify whatever trails the last contract token.
      if (lastIndex < text.length) {
        out.push(
          ...linkifyTickers(text.slice(lastIndex), msgIndex, `${keyBase}-tail`)
        );
      }
      return out;
    },
    [contractCardData, handleContractClick, linkifyTickers]
  );

  /**
   * Render an assistant message's settled text with position, contract, and
   * ticker chips. Precedence is `position:` → `contract:` → bare ticker.
   * Position tokens (`[label](position:…)`) are matched FIRST: they carry an
   * opaque querystring body (`[^)\s]+`) and a distinct `position:` prefix, so
   * they can never collide with a `contract:` token or a bare ticker. The gaps
   * between position tokens are then handed to linkifyContracts (contracts +
   * tickers). Only already-revealed text is passed in, so a chip never forms
   * mid-word during streaming. Position tokens known 'notfound' fall back to
   * rendering just their label as plain text.
   */
  const renderLinkified = useCallback(
    (rawText: string, msgIndex: number): React.ReactNode[] => {
      // Strip any markdown the model slipped in before tokenizing — this surface
      // shows plain text, so ** / "1." would otherwise render literally.
      const text = stripMarkdown(rawText);
      const out: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      POSITION_RE.lastIndex = 0;
      let pKey = 0;
      while ((match = POSITION_RE.exec(text)) !== null) {
        const [, label, qs] = match;
        // Contract/ticker-linkify the plain text before this position token.
        if (match.index > lastIndex) {
          out.push(
            ...linkifyContracts(
              text.slice(lastIndex, match.index),
              msgIndex,
              `${msgIndex}-p${pKey}`
            )
          );
        }
        const params = parsePositionParams(qs);
        if (!params) {
          // Unparseable querystring — render the human label as plain text.
          out.push(label);
        } else {
          const key = positionKey(params);
          if (positionCardData[key] === 'notfound') {
            out.push(label);
          } else {
            out.push(
              <button
                key={`ps-${msgIndex}-${pKey++}`}
                type="button"
                className="cch-position"
                onClick={() => handlePositionClick(params, msgIndex)}
              >
                {label}
              </button>
            );
          }
        }
        lastIndex = match.index + match[0].length;
      }
      // Contract/ticker-linkify whatever trails the last position token.
      if (lastIndex < text.length) {
        out.push(
          ...linkifyContracts(
            text.slice(lastIndex),
            msgIndex,
            `${msgIndex}-ptail`
          )
        );
      }
      return out.length ? out : [text];
    },
    [positionCardData, handlePositionClick, linkifyContracts]
  );

  /**
   * Auto-open the position decision card the moment an assistant message
   * SETTLES with a position token — so the roll/hold/close card appears in the
   * panel without a click. Only fires when NOT streaming (the token text is only
   * complete once the bubble has settled); scans the last assistant message,
   * finds the FIRST parseable position token, and runs the same open path a
   * click would (handlePositionClick) — so caching, the desktop panel, the
   * mobile inline reveal, and 404-demotion all behave identically to a click.
   *
   * Dedupe: each (msgIndex, positionKey) auto-opens at most once (tracked in
   * autoOpenedPositionsRef), so streaming ticks / re-renders never re-fire it.
   * A new position token in a later message has a different msgIndex, so it
   * auto-opens and replaces the panel artifact. ONLY position tokens are scanned
   * here — ticker/contract chips stay click-to-open and never auto-open.
   */
  useEffect(() => {
    if (streaming || messages.length === 0) return;
    // Desktop only: the card auto-opens in the always-present right panel, which
    // has room to spare. On mobile the card opens in a bottom sheet that COVERS
    // the chat — popping it up unprompted (and overriding whatever card the user
    // tapped mid-stream) is disruptive, so let mobile users tap to open.
    if (typeof window !== 'undefined'
        && !window.matchMedia('(min-width: 980px)').matches) return;
    const i = messages.length - 1;
    const last = messages[i];
    if (last.role !== 'assistant' || last.isError) return;

    // Find the first parseable position token in the settled text.
    POSITION_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = POSITION_RE.exec(last.content)) !== null) {
      const params = parsePositionParams(match[2]);
      if (!params) continue;
      const dedupeKey = `${i}|${positionKey(params)}`;
      if (autoOpenedPositionsRef.current.has(dedupeKey)) return; // already auto-opened
      autoOpenedPositionsRef.current.add(dedupeKey);
      void handlePositionClick(params, i);
      return; // first position token only
    }
  }, [messages, streaming, handlePositionClick]);

  // On mount: if this visitor already converted, never auto-nudge them again.
  useEffect(() => {
    try {
      if (localStorage.getItem(LS_UNLOCKED) === '1') {
        setUnlocked(true);
        unlockedRef.current = true;
        nudgeShownRef.current = true;
        const stored = localStorage.getItem(LS_EMAIL);
        if (stored) setEmail(stored);
      }
    } catch {
      /* localStorage unavailable — treat as not-unlocked */
    }
  }, []);

  // --- word-by-word reveal plumbing -------------------------------------
  // Network text lands in `pendingRef` (full text received so far for the
  // currently-streaming assistant turn). A timer drains it word-by-word
  // into the rendered message so DISPLAY is decoupled from network arrival.
  const pendingRef = useRef('');   // full text received so far
  const shownRef = useRef('');     // text already revealed
  const revealTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamDoneRef = useRef(false); // network finished; drain faster

  const stopReveal = useCallback(() => {
    if (revealTimer.current !== null) {
      clearInterval(revealTimer.current);
      revealTimer.current = null;
    }
  }, []);

  /** Reset all reveal buffers + timer (new message / error / unmount). */
  const resetReveal = useCallback(() => {
    stopReveal();
    pendingRef.current = '';
    shownRef.current = '';
    streamDoneRef.current = false;
  }, [stopReveal]);

  // clear the timer on unmount so we never tick into an unmounted tree
  useEffect(() => () => stopReveal(), [stopReveal]);

  useEffect(() => {
    document.title = 'Ask the covered-call coach — Babylon';
  }, []);

  // Swap the roll-vs-assign starter chip for a LIVE, currently-in-the-money
  // scenario so its numbers stay current (the static chip is just the fallback).
  // Best-effort: any failure leaves the evergreen fallback in place.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/coach/sample-roll-scenario`);
        if (!res.ok) return;
        const data = await res.json();
        const text = typeof data?.text === 'string' ? data.text.trim() : '';
        if (cancelled || !text) return;
        setStarters((prev) => {
          const next = prev.slice();
          next[1] = text;
          return next;
        });
      } catch {
        /* keep the evergreen fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // On a NEW turn, anchor the user's just-sent question to the TOP of the view
  // so the reader starts at the BEGINNING of the answer — then leave the scroll
  // alone. Critically, we reposition only when the message COUNT grows (a new
  // turn), NOT on the streaming content updates that grow the last message —
  // otherwise every token would yank the view back and the reader couldn't
  // scroll down to follow the answer.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const count = messages.length;
    const grew = count > anchoredCount.current;
    anchoredCount.current = count;

    // Empty state: pin to the TOP so the prompt + first starter chip are visible.
    if (count === 0) {
      el.scrollTop = 0;
      return;
    }
    // Streaming / content-only updates (no new turn): never auto-scroll.
    if (!grew) return;

    // New turn: bring the latest user question to the top, response below it.
    const userMsgs = el.querySelectorAll('.cch-msg--user');
    const lastUser = userMsgs[userMsgs.length - 1] as HTMLElement | undefined;
    if (lastUser) {
      lastUser.scrollIntoView({ block: 'start' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  /** Push the currently-revealed text into the last (assistant) message. */
  const flushShown = useCallback(() => {
    const shown = shownRef.current;
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant' || last.isError) return prev;
      if (last.content === shown) return prev;
      const next = prev.slice();
      next[next.length - 1] = { ...last, content: shown };
      return next;
    });
  }, []);

  /** Start (or keep) the drip timer that reveals pending text by word. */
  const ensureReveal = useCallback(() => {
    if (revealTimer.current !== null) return;
    revealTimer.current = setInterval(() => {
      const pending = pendingRef.current;
      const shown = shownRef.current;
      if (shown.length >= pending.length) {
        // caught up — stop only once the network is also done.
        if (streamDoneRef.current) stopReveal();
        return;
      }
      // Reveal a whole word = run of leading whitespace + the following
      // non-space run. After the network is done, reveal a few words per
      // tick so the remaining buffer drains quickly (but never instantly).
      const wordsPerTick = streamDoneRef.current ? 3 : 1;
      let i = shown.length;
      for (let w = 0; w < wordsPerTick && i < pending.length; w++) {
        while (i < pending.length && /\s/.test(pending[i])) i++;
        while (i < pending.length && !/\s/.test(pending[i])) i++;
      }
      shownRef.current = pending.slice(0, i);
      flushShown();
    }, 32);
  }, [flushShown, stopReveal]);

  /** Buffer streamed text and let the reveal timer drip it out. */
  const appendToLast = useCallback(
    (chunk: string) => {
      pendingRef.current += chunk;
      ensureReveal();
    },
    [ensureReveal]
  );

  /** Replace the last (assistant) message with an error/notice bubble. */
  const setLastError = useCallback(
    (text: string) => {
      resetReveal();
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = prev.slice();
        next[next.length - 1] = { role: 'assistant', content: text, isError: true };
        return next;
      });
    },
    [resetReveal]
  );

  /** Append a plain (non-error) assistant-style note to the transcript. */
  const pushAssistantNote = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
  }, []);

  /**
   * POST the email to /unlock. Returns true on {ok:true}. Marks the visitor
   * unlocked + persists to localStorage on success. Used by both the modal
   * submit and the silent re-unlock retry after a backend reset.
   */
  const postUnlock = useCallback(
    async (addr: string): Promise<boolean> => {
      const res = await fetch(`${API_URL}/api/public/coach/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: addr,
          utm_source: utmSource ? utmSource.slice(0, 60) : null,
          utm_campaign: (utmCampaign || 'ai_coach').slice(0, 120),
          referrer: document.referrer ? document.referrer.slice(0, 2000) : null,
          // Stitch the email back to this visit's pageview + questions.
          session_id: sessionIdRef.current,
        }),
      });
      const data: { ok?: boolean; error?: string } = await res
        .json()
        .catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setUnlocked(true);
      unlockedRef.current = true;
      trackGoal('coach_email_submit'); // funnel: visitor → email lead captured
      try {
        localStorage.setItem(LS_UNLOCKED, '1');
        localStorage.setItem(LS_EMAIL, addr);
      } catch {
        /* persistence is best-effort */
      }
      return true;
    },
    [utmSource, utmCampaign]
  );

  const send = useCallback(
    async (text: string, isRetry = false, baseHistory?: Message[]) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;

      // Leaving the income answer for a free-text turn → back to normal chat mode
      // (restores the right deep-dive panel; clears the inline income card).
      setIncomeResult(null);

      // Build the conversation we POST: full history + this user turn.
      // Callers may pass an explicit `baseHistory` (e.g. the post-unlock
      // auto-answer, which prunes the stranded gate-notice turn) — otherwise
      // use the current transcript.
      const userMsg: Message = { role: 'user', content: trimmed };
      const history = [...(baseHistory ?? messages), userMsg];

      // Count this as a user question (but not on a silent re-unlock retry,
      // where we re-send the same turn). Used to trigger the question-3 nudge.
      if (!isRetry) {
        questionCountRef.current += 1;
        // Funnel: engagement volume + Q1->Q2->Q3 drop-off (the question number
        // is a Plausible prop, so we see where visitors quit before the gate).
        trackGoal('coach_question_asked', { n: questionCountRef.current });
      }

      // Fresh reveal state for this turn (drop anything still buffered).
      resetReveal();

      // Optimistic UI: show user turn + an empty assistant bubble to stream into.
      setMessages([...history, { role: 'assistant', content: '' }]);
      setInput('');
      setStreaming(true);

      // Shared handler for "free cap reached". If the visitor is unlocked in
      // localStorage but the backend reset (redeploy), silently re-POST /unlock
      // with the stored email and retry the turn ONCE before gating.
      const handleRateLimited = async (): Promise<boolean> => {
        if (!isRetry && unlockedRef.current) {
          let stored: string | null = null;
          try {
            stored = localStorage.getItem(LS_EMAIL);
          } catch {
            stored = null;
          }
          if (stored) {
            try {
              await postUnlock(stored);
              setStreaming(false);
              await send(trimmed, true);
              return true; // handled via retry
            } catch {
              /* fall through to the gate */
            }
          }
        }
        // Remember the blocked question so a successful unlock can auto-answer
        // it. Skip on a retry — that text is already being (re)sent, not stranded.
        if (!isRetry) pendingQuestionRef.current = trimmed;
        setLastError(RATE_LIMIT_TEXT);
        setHasAnswered(true);
        setModalMode('gate');
        return false;
      };

      try {
        const res = await fetch(`${API_URL}/api/public/coach/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: history.map((m) => ({ role: m.role, content: m.content })),
            // Funnel telemetry: stitches each question to the visit + carries UTM
            // so the server logs a 'question' event (with text) per accepted turn.
            ...buildAttribution(),
          }),
        });

        if (res.status === 429) {
          await handleRateLimited();
          return;
        }
        if (!res.ok || !res.body) {
          setLastError("Something broke on my end — give it another go in a moment.");
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let gotText = false;
        let done = false;

        while (!done) {
          const { value, done: rdDone } = await reader.read();
          if (rdDone) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by a blank line.
          const parts = buffer.split('\n\n');
          buffer = parts.pop() ?? ''; // keep the trailing partial event

          for (const part of parts) {
            // an event block may have multiple lines; we only care about data:
            const dataLine = part
              .split('\n')
              .find((l) => l.startsWith('data:'));
            if (!dataLine) continue;
            const json = dataLine.slice(5).trim();
            if (!json) continue;

            let evt: { type?: string; content?: string; reason?: string };
            try {
              evt = JSON.parse(json);
            } catch {
              continue; // ignore unparseable fragment
            }

            if (evt.type === 'text' && evt.content) {
              gotText = true;
              appendToLast(evt.content);
            } else if (evt.type === 'error') {
              const content = evt.content || RATE_LIMIT_TEXT;
              // The server marks the daily cap (email already given, extended
              // daily allowance used up) with reason='register_required'. The
              // only way forward is an account — show the register gate. Checked
              // BEFORE rate_limit so it never falls into the email-gate path.
              if (evt.reason === 'register_required') {
                done = true;
                streamDoneRef.current = true;
                stopReveal();
                setStreaming(false);
                setLastError(content || REGISTER_GATE_TEXT);
                setHasAnswered(true);
                setModalMode('register');
                return;
              }
              // The server marks the free cap with a structured reason='rate_limit'.
              // Route those to the gate (+ silent re-unlock). The text heuristic is
              // only a fallback for older server builds.
              const isRateLimit =
                evt.reason === 'rate_limit' ||
                (/email/i.test(content) && /limit|going|free/i.test(content));
              if (isRateLimit) {
                done = true;
                streamDoneRef.current = true;
                stopReveal();
                setStreaming(false);
                await handleRateLimited();
                return;
              }
              setLastError(content);
              setHasAnswered(true);
              done = true;
              break;
            } else if (evt.type === 'done') {
              done = true;
              break;
            }
          }
        }

        // Network is finished — let the reveal timer drain whatever's left,
        // then stop. (No instant dump; it catches up quickly on its own.)
        streamDoneRef.current = true;
        if (pendingRef.current.length > shownRef.current.length) {
          ensureReveal();
        } else {
          stopReveal();
        }

        // If the stream closed with nothing, surface a graceful notice.
        if (!gotText) {
          setMessages((prev) => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            if (last.role === 'assistant' && last.content === '' && !last.isError) {
              const next = prev.slice();
              next[next.length - 1] = {
                role: 'assistant',
                content: "I didn't get a response that time — try asking again.",
                isError: true,
              };
              return next;
            }
            return prev;
          });
        }
        setHasAnswered(true);

        // After the 3rd question's answer finishes, invite the visitor to share
        // their email — once per session, and never if they've already unlocked.
        if (
          !isRetry &&
          !unlockedRef.current &&
          !nudgeShownRef.current &&
          questionCountRef.current >= FREE_QUESTIONS
        ) {
          nudgeShownRef.current = true;
          setModalMode('nudge');
        }
      } catch {
        setLastError("Couldn't reach the coach — check your connection and try again.");
      } finally {
        setStreaming(false);
      }
    },
    [
      messages,
      streaming,
      appendToLast,
      setLastError,
      resetReveal,
      ensureReveal,
      stopReveal,
      postUnlock,
    ]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  /**
   * Income form submit — DETERMINISTIC path (no LLM). POSTs the structured
   * position to /income-split (the real engine), then renders the income CARD in
   * the panel/sheet + a short templated narration in chat. This is what fixes the
   * wrong/wordy LLM answer: the math comes from the engine, every time.
   */
  const submitIncome = useCallback(
    async (f: IncomeFormFields) => {
      if (streaming) return;
      const sym = f.symbol.toUpperCase();
      const userText = `${sym} $${f.strike} ${f.type} · ${f.expiry} · is my income real?`;
      const userMsg: Message = { role: 'user', content: userText };
      setMessages([userMsg, { role: 'assistant', content: 'Running the numbers on your position…' }]);
      setStreaming(true);
      setHasAnswered(true);
      questionCountRef.current += 1;
      trackGoal('coach_question_asked', { n: questionCountRef.current });

      try {
        const res = await fetch(`${API_URL}/api/public/coach/income-split`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            symbol: sym,
            strike: parseFloat(f.strike),
            expiry: f.expiry,
            type: f.type,
            entry_premium: f.premium ? parseFloat(f.premium) : null,
            cost_basis: f.costBasis ? parseFloat(f.costBasis) : null,
            entry_date: f.entryDate || null,
            num_contracts: f.contracts ? parseInt(f.contracts, 10) : 1,
            ...buildAttribution(),
          }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || data.error) {
          const msg =
            (data && data.notes && data.notes[0]) ||
            "I couldn't compute that — double-check the ticker, strike and expiry, or just type your question below.";
          setMessages([userMsg, { role: 'assistant', content: msg, isError: true }]);
          return;
        }
        setCurrentArtifact(null);
        setIncomeResult(data as IncomeData);
        setMessages([userMsg, { role: 'assistant', content: incomeNarration(data as IncomeData) }]);
      } catch {
        setMessages([
          userMsg,
          { role: 'assistant', content: 'Something broke on my end — give it another go in a moment.', isError: true },
        ]);
      } finally {
        setStreaming(false);
      }
    },
    [streaming, buildAttribution],
  );

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEmailError(null);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid || emailSubmitting) return;
    setEmailSubmitting(true);
    setEmailError(null);
    try {
      await postUnlock(email.trim());
      closeModal();
      pushAssistantNote("You're all set — keep going. No wall, more questions.");
      // If the gate was triggered by a blocked send, auto-answer that pending
      // question now that the IP is unlocked server-side — the user shouldn't
      // have to re-type it. Reuse the normal send path as a retry (isRetry=true
      // so it doesn't re-count the question or re-arm the gate-capture branch),
      // and clear the ref first so this fires at most once (loop guard).
      const pending = pendingQuestionRef.current;
      pendingQuestionRef.current = null;
      if (pending) {
        // Prune the stranded turn (its user bubble + the gate-notice assistant
        // bubble) and re-send through the normal machinery as a retry. Passing
        // the pruned array as `baseHistory` makes `send` re-add the user turn
        // with a fresh streaming bubble — no duplicate turn, no leftover notice,
        // and no stale-closure / setTimeout dance. `isRetry=true` keeps it from
        // re-counting the question or re-arming the gate-capture branch; the ref
        // is already cleared, so this can fire at most once (loop guard).
        const base = messages.slice();
        if (
          base.length >= 2 &&
          base[base.length - 1].role === 'assistant' &&
          base[base.length - 1].isError &&
          base[base.length - 2].role === 'user'
        ) {
          base.splice(base.length - 2, 2);
        }
        void send(pending, true, base);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setEmailError(
        /please enter a valid email/i.test(msg)
          ? msg
          : "Couldn't unlock that — please try again."
      );
    } finally {
      setEmailSubmitting(false);
    }
  };

  // Dismiss the modal on Esc while it's open.
  useEffect(() => {
    if (!modalMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalMode, closeModal]);

  const isEmpty = messages.length === 0;

  /* The starter chips appear in two places: centered in the empty state, and
     relocated to the left rail once a conversation is active. Same send path.
     For a roll/setup ad, surface that use case's matching question first so the
     landing leads with the exact thing the ad teased. */
  const leadChip = USE_CASE_LEAD_CHIP[useCase];
  const orderedIdx: number[] = (() => {
    const all = starters.map((_, i) => i);
    // Income: the form IS the income path — drop the redundant income chip (0) so
    // there's no second, LLM-routed income answer competing with the card.
    if (useCase === 'income') return all.filter((i) => i !== 0);
    if (leadChip != null && starters[leadChip]) {
      return [leadChip, ...all.filter((i) => i !== leadChip)];
    }
    return all;
  })();
  // Empty-state (landing): full-text chips — cold visitors read the actual question.
  const starterChips = (
    <div className="cch-starters">
      {orderedIdx.map((i) => (
        <button
          key={i}
          type="button"
          className="cch-starter"
          disabled={streaming}
          onClick={() => send(starters[i])}
        >
          {starters[i]}
        </button>
      ))}
    </div>
  );
  // Active conversation left rail: compact icon + short label (saves space, less
  // wordy). Tapping still sends the full starter question via the same path.
  const railChips = (
    <div className="cch-rail-icons">
      {orderedIdx.map((i) => {
        const meta = STARTER_META[i];
        if (!meta) return null;
        const Icon = meta.Icon;
        return (
          <button
            key={i}
            type="button"
            className="cch-rail-icon"
            disabled={streaming}
            title={starters[i]}
            onClick={() => send(starters[i])}
          >
            <Icon className="cch-rail-icon-svg" aria-hidden="true" />
            <span className="cch-rail-icon-label">{meta.label}</span>
          </button>
        );
      })}
    </div>
  );

  /* Chat surface — the message list. Reused inside both layouts (empty +
     active). Fills the available vertical space and scrolls. */
  const chatSurface = (
    <section className="cch-chat cch-reveal" style={reveal(4)}>
      <div className="cch-messages" ref={messagesRef}>
        {isEmpty ? (
          /* Empty state lives in the conversation area: prompt + starters.
             These disappear once the first message is sent. */
          <div className="cch-emptystate">
            <p className="cch-empty">
              Ask a question below, or tap one of these to start — I'll answer with the data.
            </p>
            {starterChips}
          </div>
        ) : (
          messages.map((m, i) => {
            const isLast = i === messages.length - 1;
            const showCaret =
              streaming && isLast && m.role === 'assistant' && !m.isError;
            // Linkify ALL assistant text (not user turns, not error bubbles),
            // INCLUDING the bubble that's actively streaming — so tickers and
            // contract / decision chips are clickable the moment they appear,
            // not only once the answer finishes. For the streaming bubble we
            // linkify a safe prefix (streamingSafeText) that holds back a
            // half-formed `[label](…)` token until it completes, so a chip never
            // forms mid-word. Settling re-renders the full text — flicker-free.
            const isAssistant = m.role === 'assistant' && !m.isError;
            return (
              <div
                key={i}
                className={`cch-msg ${
                  m.role === 'user' ? 'cch-msg--user' : 'cch-msg--assistant'
                }`}
              >
                <span className="cch-msg-role">
                  {m.role === 'user' ? 'You' : 'Coach'}
                </span>
                <div
                  className={`cch-bubble${m.isError ? ' cch-bubble--error' : ''}`}
                >
                  {isAssistant
                    ? renderLinkified(showCaret ? streamingSafeText(m.content) : m.content, i)
                    : m.content}
                  {showCaret && <span className="cch-caret" aria-hidden="true" />}
                </div>
                {/* On mobile (<980px) the tapped card opens in the bottom sheet
                    (see .cch-sheet near the modal), not inline under the message —
                    so it's never stranded below the fold of a long answer. */}
              </div>
            );
          })
        )}
      </div>
    </section>
  );

  /* Composer — pinned at the bottom of the center column at all times. */
  const composer = (
    <div className="cch-bottom">
      <form className="cch-composer" onSubmit={handleSubmit}>
        <textarea
          className="cch-input"
          rows={2}
          placeholder="Ask about a covered call — a ticker, a roll decision, your income…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={streaming}
          aria-label="Ask the coach"
        />
        <button
          className="cch-send"
          type="submit"
          disabled={streaming || !input.trim()}
        >
          {streaming ? '…' : 'Send'}
        </button>
      </form>

      <footer className="cch-footer">
        Built by one covered-call trader who got tired of guessing. Not advice. Not a fund. Just
        honest math on your own positions. © 2026 Babylon.
      </footer>
    </div>
  );

  // Current artifact state for the desktop right panel — keyed by kind.
  const artifactState: CardState | undefined =
    currentArtifact?.kind === 'stock'
      ? cardData[currentArtifact.symbol]
      : undefined;
  const contractArtifactState: ContractCardState | undefined =
    currentArtifact?.kind === 'contract'
      ? contractCardData[currentArtifact.key]
      : undefined;
  const positionArtifactState: PositionCardState | undefined =
    currentArtifact?.kind === 'position'
      ? positionCardData[currentArtifact.key]
      : undefined;
  // The panel shows the empty hint when there's no artifact, or when the
  // current artifact resolved to 'notfound' (any kind).
  const artifactNotFound =
    (currentArtifact?.kind === 'stock' && artifactState === 'notfound') ||
    (currentArtifact?.kind === 'contract' && contractArtifactState === 'notfound') ||
    (currentArtifact?.kind === 'position' && positionArtifactState === 'notfound');
  // The label shown on the panel's top bar.
  const artifactLabel =
    currentArtifact?.kind === 'stock'
      ? currentArtifact.symbol
      : currentArtifact?.kind === 'income'
      ? `${currentArtifact.data.symbol} · is my income real?`
      : currentArtifact?.kind === 'contract' ||
        currentArtifact?.kind === 'position'
      ? currentArtifact.params.symbol
      : '';

  return (
    <div className="cch-page">
      <main className={`cch-container${isEmpty ? '' : ' cch-container--active'}${incomeResult ? ' cch-container--income' : ''}`}>
        {isEmpty ? (
          /* ----- EMPTY STATE: single centered column (unchanged) ----- */
          <>
            <div className="cch-top">
              <div className="cch-wordmark cch-reveal" style={reveal(0)}>
                babylon<span>.markets</span>
              </div>
              <header className="cch-hero">
                <h1 className="cch-h1 cch-reveal" style={reveal(1)}>
                  {hero.h1}
                </h1>
                <p className="cch-deck cch-reveal" style={reveal(2)}>
                  {hero.deck}
                </p>
                <div className="cch-split-rule cch-reveal" style={reveal(3)} aria-hidden="true">
                  <span className="cch-split-phantom" />
                  <span className="cch-split-real" />
                </div>
              </header>
            </div>
            {/* Income use case (phantom_income ad): the zero-tap income form is the
                dominant first action. Other use cases lead with their matching
                starter chip instead (reordered above), routed through the tools. */}
            {useCase === 'income' && (
              <div className="cch-reveal" style={reveal(3)}>
                <CoachIncomeForm onSubmit={submitIncome} disabled={streaming} />
              </div>
            )}
            {chatSurface}
            {composer}
          </>
        ) : (
          /* ----- ACTIVE STATE: three-zone artifact layout (desktop) /
                   single column (mobile, via CSS) ----- */
          <div className={`cch-stage${incomeResult ? ' cch-stage--income' : ''}`}>
            {/* LEFT rail — compact identity + relocated starter chips. */}
            <aside className="cch-rail">
              <div className="cch-wordmark cch-reveal" style={reveal(0)}>
                babylon<span>.markets</span>
              </div>
              <div className="cch-rail-starters">
                <p className="cch-rail-starters-label">Try asking</p>
                {railChips}
              </div>
              {/* Persistent conversion CTA — always visible in the rail (desktop).
                  Mobile (rail hidden) uses the under-card CTA instead. */}
              <button
                type="button"
                className="cch-rail-cta"
                onClick={() => {
                  trackGoal('coach_register_click', { plan: 'free', surface: 'rail' });
                  trackEvent('register_click', buildAttribution());
                  navigate(registerPath());
                }}
              >
                Start free
              </button>
            </aside>

            {/* CENTER — the conversation + (for the income answer) the 2-col income
                card inline, so the answer lives where you're reading + the page
                isn't right-heavy. Pinned composer below. */}
            <div className="cch-center">
              <div className="cch-center-scroll">
                {chatSurface}
                {incomeResult && (
                  <>
                    <div className="cch-inc-inline">
                      <PublicIncomeCard data={incomeResult} />
                    </div>
                    <div className="cch-inc-cta">
                      <p className="cch-inc-cta-copy">
                        This is one position. Babylon runs this on your whole book — live position
                        tracking, your own Discipline rules, and the full options chain.
                      </p>
                      <button
                        type="button"
                        className="cch-inc-cta-btn"
                        onClick={() => {
                          trackGoal('coach_register_click', { plan: 'free', surface: 'income' });
                          trackEvent('register_click', buildAttribution());
                          navigate(registerPath());
                        }}
                      >
                        Start free
                      </button>
                    </div>
                  </>
                )}
              </div>
              {composer}
            </div>

            {/* RIGHT — the deep-dive artifact panel (free-text flow only; hidden for
                the income answer, which is centered). Desktop only; CSS hides <980px. */}
            {!incomeResult && (
            <aside className="cch-artifact" aria-label="Data card">
              {currentArtifact && !artifactNotFound ? (
                <div className="cch-artifact-card">
                  <div className="cch-artifact-bar">
                    <span className="cch-artifact-bar-label">
                      {artifactLabel}
                    </span>
                    <button
                      type="button"
                      className="cch-artifact-close"
                      aria-label="Clear card"
                      onClick={() => setCurrentArtifact(null)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="cch-artifact-body">
                    {currentArtifact.kind === 'stock'
                      ? renderArtifactBody(artifactState)
                      : currentArtifact.kind === 'contract'
                      ? renderContractBody(contractArtifactState)
                      : currentArtifact.kind === 'income'
                      ? <PublicIncomeCard data={currentArtifact.data} />
                      : renderPositionBody(positionArtifactState)}
                  </div>
                </div>
              ) : (
                <div className="cch-artifact-hint">
                  Click any ticker, contract, or decision link in the conversation to see its card here.
                </div>
              )}

              {/* Persistent upsell — the right panel always carries a quiet
                  reminder that this canvas is a slice of the full product. */}
              <div className="cch-artifact-promo">
                <p className="cch-artifact-promo-copy">
                  This coach answers one question at a time. The full app runs
                  this on your whole portfolio — live position tracking, your own
                  Discipline rules, and the complete options chain.
                </p>
                <button
                  type="button"
                  className="cch-artifact-promo-cta"
                  onClick={() => {
                    trackGoal('coach_register_click', { plan: 'free', surface: 'promo' });
                    trackEvent('register_click', buildAttribution());
                    navigate(registerPath());
                  }}
                >
                  Start free
                </button>
              </div>
            </aside>
            )}
          </div>
        )}
      </main>

      {/* Mobile bottom sheet — the <980px surface for a tapped ticker /
          contract / decision card. Driven by the SAME `currentArtifact` as the
          desktop right panel; CSS hides it ≥980px (desktop uses the panel).
          Slides up over the conversation so the card is never stranded below the
          fold of a long answer. Dismiss: backdrop tap, the handle, the × button,
          or a swipe down. */}
      {currentArtifact && !artifactNotFound && (
        <div
          className="cch-sheet-backdrop"
          role="presentation"
          onClick={() => setCurrentArtifact(null)}
        >
          <div
            className="cch-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={`${artifactLabel} card`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header is the swipe-to-dismiss grip — handlers live here, NOT on
                the scrollable body, so scrolling a tall card never dismisses. */}
            <div
              className="cch-sheet-grip"
              onTouchStart={(e) => {
                sheetTouchY.current = e.touches[0]?.clientY ?? null;
              }}
              onTouchEnd={(e) => {
                const start = sheetTouchY.current;
                sheetTouchY.current = null;
                const end = e.changedTouches[0]?.clientY;
                if (start != null && end != null && end - start > 70) {
                  setCurrentArtifact(null);
                }
              }}
            >
              {/* Compact header: a centered grab handle (drag down to dismiss)
                  + a corner close. No ticker label — the card body already
                  shows it, so repeating it just wastes vertical space. */}
              <div className="cch-sheet-handle" aria-hidden="true" />
              <button
                type="button"
                className="cch-artifact-close cch-sheet-close"
                aria-label="Close card"
                onClick={() => setCurrentArtifact(null)}
              >
                ×
              </button>
            </div>
            <div className="cch-sheet-body">
              {currentArtifact.kind === 'stock'
                ? renderArtifactBody(artifactState)
                : currentArtifact.kind === 'contract'
                ? renderContractBody(contractArtifactState)
                : currentArtifact.kind === 'income'
                ? <PublicIncomeCard data={currentArtifact.data} />
                : renderPositionBody(positionArtifactState)}
            </div>
          </div>
        </div>
      )}

      {/* Unlock / gate modal — the single capture point. Three modes:
          'nudge' (auto, after Q3) and 'gate' (hard, after the free total) are
          the EMAIL gate; 'register' is the final gate after the extended
          lifetime total is used up — no email field, just a CTA to /register. */}
      {modalMode && (
        <div
          className="cch-modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="cch-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cch-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {modalMode === 'register' ? (
              /* ----- REGISTER GATE: today's allowance used up. No email field
                       (they already gave one) — just a CTA to the sign-up page.
                       Dismissible, but it re-fires on the next send because the
                       server keeps returning register_required. ----- */
              <>
                <h2 className="cch-modal-title" id="cch-modal-title">
                  You've used your free questions for today
                </h2>
                <p className="cch-modal-body">
                  Create a free account to keep going with the coach — or unlock
                  the full app with Pro.
                </p>
                <button
                  type="button"
                  className="cch-modal-submit cch-modal-cta"
                  onClick={() => {
                    trackGoal('coach_register_click', { plan: 'free', surface: 'gate' });
                    trackEvent('register_click', buildAttribution());
                    navigate(registerPath());
                  }}
                >
                  Start free
                </button>

                {/* Pro exposure for the warmest leads: someone who's asked ~15
                    coach questions already knows the value. Surface Pro (and its
                    no-card trial) right here instead of routing only to Free. */}
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 16,
                    borderTop: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <p
                    style={{
                      margin: '0 0 10px',
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: '#b8b8b4',
                    }}
                  >
                    Go Pro for unlimited coaching on your whole portfolio — the
                    roll-vs-assign engine, real-yield ("is my income real?"), and
                    earnings guardrails. 14-day free trial, no card.
                  </p>
                  <button
                    type="button"
                    className="cch-modal-submit cch-modal-cta"
                    style={{
                      background: 'transparent',
                      border: '1.5px solid #2962ff',
                      color: '#6f9bff',
                    }}
                    onClick={() => {
                      trackGoal('coach_pro_click', { plan: 'pro', surface: 'gate' });
                      navigate(registerPath('pro'));
                    }}
                  >
                    Try Pro free for 14 days
                  </button>
                </div>

                <button
                  type="button"
                  className="cch-modal-dismiss"
                  onClick={closeModal}
                >
                  Maybe later
                </button>
              </>
            ) : (
              /* ----- EMAIL GATE (nudge / gate): unchanged. ----- */
              <>
                <h2 className="cch-modal-title" id="cch-modal-title">
                  {modalMode === 'gate'
                    ? "You've used your 3 free questions for today"
                    : 'Finding this useful?'}
                </h2>
                <p className="cch-modal-body">
                  {modalMode === 'gate'
                    ? "Drop your email to keep going — it's free, and it lifts the limit right away."
                    : "Share your email and we'll extend your free usage right away — more questions, no wall."}
                </p>
                <form className="cch-modal-form" onSubmit={handleEmailSubmit}>
                  <input
                    className="cch-modal-input"
                    type="email"
                    maxLength={255}
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Your email"
                    autoFocus
                  />
                  <button
                    className="cch-modal-submit"
                    type="submit"
                    disabled={!emailValid || emailSubmitting}
                  >
                    {emailSubmitting ? 'Unlocking…' : 'Extend my access'}
                  </button>
                </form>
                {emailError && (
                  <p className="cch-modal-error" role="alert">
                    {emailError}
                  </p>
                )}
                <button
                  type="button"
                  className="cch-modal-dismiss"
                  onClick={closeModal}
                >
                  Maybe later
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCoach;
